import { objectWithoutProperties as _objectWithoutProperties, objectSpread2 as _objectSpread2, asyncToGenerator as _asyncToGenerator, regeneratorRuntime as _regeneratorRuntime } from '../_virtual/_rollupPluginBabelHelpers.js';
import { readEntity } from './apiActions.js';
import { setActiveSearch, setActiveSort, setActiveFilters, setActivePage, deleteEntity, updateEntity, createEntity, selectEntity } from './coreApiActions.js';
import { clearMessages, displaySuccessMessage, displayErrorMessages, displayErrorMessage, displayError } from './messagesActions.js';
var _excluded = ["entity", "entityAlias", "includes", "fields", "basePath", "host", "token", "skipApiExtension", "additionalParams"];
/**
 * Creates a set of common entity actions
 * @param {string} entity - the entity type the actions should be created for
 * @param {string|Array|object} includes - the includes to pass to read/select entity
 * @param {string|number} id - the id of the entity to load (for member specific containers)
 * @param {string|number} idParam - the param to use for an id (for member specific containers)
 * @param {object} fields - the fields to pass to read/select entity
 * @param {string} basePath - overrides the base path for all of the actions
 * @param {string} host - overrides the host for all of the actions
 * @param {string} token - sends an access token with all of the actions
 * @param {object} config - set additional configuration options through props named after the returned actions
 * @param {boolean} skipApiExtension - removes the .json extension on API calls.
 * @param {object} additionalParams - object to add as a param to the request in &key=value format.
 * @returns {object} with the following actions: loadCollection, selectMember, createMember, updateMember, deleteMember
 */

function genericEntityActionCreators() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      entity = _ref.entity,
      entityAlias = _ref.entityAlias,
      includes = _ref.includes,
      fields = _ref.fields,
      basePath = _ref.basePath,
      host = _ref.host,
      token = _ref.token,
      skipApiExtension = _ref.skipApiExtension,
      additionalParams = _ref.additionalParams,
      config = _objectWithoutProperties(_ref, _excluded);

  var props = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var _props$params = props.params,
      params = _props$params === void 0 ? {} : _props$params,
      _props$router = props.router,
      router = _props$router === void 0 ? {} : _props$router;

  function getMemberId(id) {
    if (typeof id !== 'undefined') {
      return id;
    } else if (props.id) {
      return props.id;
    } else if (props.path) {
      var pathParam = /:(\w+)/i.exec(props.path);

      if (pathParam) {
        return params[pathParam[1]];
      }
    }
  }
  /**
   * Action used for loading a collection of entities using readEntity
   * @param {object} filter - key value pair of filters to add to the request
   * @param {object|Array} sorts - array of sorts objects { value: string, direction: 'asc|desc' } to add to the request
   * @param {number|object} page - page number with default page size or object { number: number, size: number } to add to the request
   * @param {object} query - query fields { name: { term: string, fields: { field1: match_type, field2: match_type, ... } } } to add to the request
   */


  function loadCollection() {
    var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
        filters = _ref2.filters,
        sorts = _ref2.sorts,
        page = _ref2.page,
        query = _ref2.query;

    return readEntity({
      entity: entity,
      entityAlias: entityAlias,
      includes: includes,
      filters: filters,
      sorts: sorts,
      page: page,
      query: query,
      fields: fields,
      basePath: basePath,
      host: host,
      token: token,
      skipJSONExtension: skipApiExtension,
      additionalParams: additionalParams
    });
  }
  /**
   * Action used for reading and selecting an entity using selectEntity
   * @param {object} filter - key value pair of filters to add to the request
   * @param {object|Array} sorts - array of sorts objects { value: string, direction: 'asc|desc' } to add to the request
   * @param {number|object} page - page number with default page size or object { number: number, size: number } to add to the request
   * @param {object} query - query fields { name: { term: string, fields: { field1: match_type, field2: match_type, ... } } } to add to the request
   * @param {string} basePathOverride - for dynamic base path needs
   */


  function selectMember(id, basePathOverride) {
    return /*#__PURE__*/function () {
      var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee(dispatch) {
        var selectMemberConfig, pathname, selectIncludes;
        return _regeneratorRuntime().wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                dispatch(selectEntity(null));
                id = getMemberId(id);
                basePath = basePathOverride || basePath;
                selectMemberConfig = config.selectMember || {};

                if (selectMemberConfig.updateRoute) {
                  pathname = "/".concat(id || '');

                  if (!router.location.pathname.endsWith(pathname)) {
                    router.push(pathname);
                  }
                }

                selectIncludes = selectMemberConfig.selectIncludes || includes;

                if (!id) {
                  _context.next = 8;
                  break;
                }

                return _context.abrupt("return", dispatch(selectEntity({
                  id: id,
                  entity: entity,
                  includes: selectIncludes,
                  fields: fields,
                  basePath: basePath,
                  host: host,
                  token: token,
                  skipJSONExtension: skipApiExtension,
                  additionalParams: additionalParams
                })));

              case 8:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      return function (_x) {
        return _ref3.apply(this, arguments);
      };
    }();
  }
  /**
   * Action used for creating a new entity using createEntity
   * @param {JsonApiDecorator} member - entity to create
   * @param {boolean} addAnother - if true, 'add' will be selected after save, else null will be selected
   * @param {string} basePathOverride - for dynamic base path needs
   */


  function createMember(member, addAnother, basePathOverride) {
    var createMemberConfig = config.createMember || {};

    if (createMemberConfig.preprocess) {
      member = createMemberConfig.preprocess(member);
    }

    basePath = basePathOverride || basePath;

    var afterSave = function afterSave(dispatch, getState, entity) {
      dispatch(selectEntity({
        id: addAnother ? 'add' : null,
        entity: entity
      }));

      if (typeof createMemberConfig.afterSave === 'function') {
        createMemberConfig.afterSave(dispatch, getState, entity, router);
      }
    };

    return createEntity(_objectSpread2(_objectSpread2({
      member: member,
      basePath: basePath,
      host: host,
      token: token
    }, createMemberConfig), {}, {
      afterSave: afterSave
    }));
  }
  /**
   * Action used for updating an existing entity using updateEntity
   * @param {JsonApiDecorator} member - entity to update
   * @param {string} basePathOverride - for dynamic base path needs
   */


  function updateMember(member, basePathOverride) {
    var updateMemberConfig = config.updateMember || {};

    if (updateMemberConfig.preprocess) {
      member = updateMemberConfig.preprocess(member);
    }

    basePath = basePathOverride || basePath;

    var afterSave = function afterSave(dispatch, getState, entity) {
      dispatch(selectEntity({
        id: null,
        entity: entity
      }));

      if (typeof updateMemberConfig.afterSave === 'function') {
        updateMemberConfig.afterSave(dispatch, getState, entity, router);
      }
    };

    return updateEntity(_objectSpread2(_objectSpread2({
      member: member,
      basePath: basePath,
      host: host,
      token: token
    }, updateMemberConfig), {}, {
      afterSave: afterSave
    }));
  }
  /**
   * Action used for deleting an existing entity using deleteEntity
   * @param {JsonApiDecorator} member - entity to delete
   */


  function deleteMember(member) {
    var deleteMemberConfig = config.deleteMember || {};

    if (deleteMemberConfig.preprocess) {
      member = deleteMemberConfig.preprocess(member);
    }

    return deleteEntity(_objectSpread2({
      member: member,
      entity: entity,
      basePath: basePath,
      host: host,
      token: token
    }, deleteMemberConfig));
  }

  return {
    loadCollection: loadCollection,
    selectMember: selectMember,
    createMember: createMember,
    updateMember: updateMember,
    deleteMember: deleteMember
  };
}

var commonActionCreators = {
  setActivePage: setActivePage,
  setActiveFilters: setActiveFilters,
  setActiveSort: setActiveSort,
  setActiveSearch: setActiveSearch,
  displayError: displayError,
  displayErrorMessage: displayErrorMessage,
  displayErrorMessages: displayErrorMessages,
  displaySuccessMessage: displaySuccessMessage,
  clearMessages: clearMessages
};
export { commonActionCreators, genericEntityActionCreators };